import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';

export default function GuidedTour({ steps, run, onComplete }) {
  const [step, setStep] = useState(0);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, height: 0 });

  const updatePosition = useCallback(() => {
    if (!run || !steps[step]) return;
    const el = document.querySelector(steps[step].target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setPos({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    }
  }, [run, step, steps]);

  useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [updatePosition]);

  useEffect(() => { setStep(0); }, [run]);

  if (!run || !steps[step]) return null;

  const current = steps[step];
  const isLast = step === steps.length - 1;
  // Position tooltip to the right of the element
  const tooltipTop = pos.top + pos.height / 2 - 60;
  const tooltipLeft = pos.left + pos.width + 16;

  return (
    <>
      {/* Overlay with hole */}
      <div className="fixed inset-0 z-[9998]" onClick={() => onComplete?.()}>
        <svg className="absolute inset-0 w-full h-full">
          <defs>
            <mask id="tour-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={pos.left - 6} y={pos.top - 6}
                width={pos.width + 12} height={pos.height + 12}
                rx="12" fill="black"
              />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#tour-mask)" />
        </svg>
        {/* Highlight ring */}
        <div
          className="absolute rounded-xl border-2 border-[#f5a800] pointer-events-none transition-all duration-300"
          style={{ top: pos.top - 6, left: pos.left - 6, width: pos.width + 12, height: pos.height + 12, boxShadow: '0 0 20px rgba(245,168,0,0.3)' }}
        />
      </div>

      {/* Tooltip */}
      <div
        className="fixed z-[9999] w-72 bg-white rounded-xl shadow-2xl border border-gray-100 p-4 transition-all duration-300"
        style={{ top: Math.max(10, Math.min(tooltipTop, window.innerHeight - 200)), left: Math.min(tooltipLeft, window.innerWidth - 300) }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-2">
          <span className="text-[10px] font-medium text-[#003366] bg-[#003366]/10 px-2 py-0.5 rounded-full">
            {step + 1} / {steps.length}
          </span>
          <button onClick={() => onComplete?.()} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-[#0b1220] leading-relaxed">{current.content}</p>
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
          <button
            onClick={() => onComplete?.()}
            className="text-[11px] text-[#8899b4] hover:text-[#526071]"
          >
            Overslaan
          </button>
          <div className="flex gap-1.5">
            {step > 0 && (
              <button
                onClick={() => setStep(s => s - 1)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-medium text-[#526071] hover:bg-gray-50"
              >
                <ChevronLeft className="w-3 h-3" /> Vorige
              </button>
            )}
            <button
              onClick={() => {
                if (isLast) onComplete?.();
                else setStep(s => s + 1);
              }}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-medium text-white bg-[#003366] hover:bg-[#00244d]"
            >
              {isLast ? 'Klaar' : 'Volgende'} {!isLast && <ChevronRight className="w-3 h-3" />}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
